import { useState } from 'react'
import type { NtTerm } from '../../core/types'
import { saveTerm } from './model'
import { Text, Select } from '../../ui/Field'
import { ErrorState } from '../../ui/States'

/**
 * Term setup — the dates and rules everything else hangs off.
 *
 * Deliberately one document rather than nine editable lists on
 * /admin/options: these are structured and interdependent (a placement
 * priority is not a dropdown option), and they are set once a year by one
 * person.
 */
export function TermSettings({ term }: { term: NtTerm }) {
  const [f, setF] = useState<NtTerm>(term)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const save = async () => {
    setBusy(true); setErr(null)
    try {
      await saveTerm(f.id, {
        label: f.label, weekLabel: f.weekLabel, startDate: f.startDate ?? '', active: true,
        deadlines: f.deadlines, rules: f.rules, lunchWaves: f.lunchWaves,
      })
      setSavedAt(new Date())
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const dl = (k: keyof NtTerm['deadlines'], v: string) =>
    setF((p) => ({ ...p, deadlines: { ...p.deadlines, [k]: v } }))
  const rule = <K extends keyof NtTerm['rules']>(k: K, v: NtTerm['rules'][K]) =>
    setF((p) => ({ ...p, rules: { ...p.rules, [k]: v } }))

  return (
    <>
      {err && <ErrorState body={`Not saved. ${err}`} />}

      <section className="card" style={{ marginTop: 'var(--s-2)' }}>
        <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-3)' }}>Dates</h3>
        <Text label="Term name" value={f.label} onChange={(v) => setF((p) => ({ ...p, label: v }))} />
        <Text label="The week itself" value={f.weekLabel}
          onChange={(v) => setF((p) => ({ ...p, weekLabel: v }))} placeholder="March 8–11, 2027" />
        <Text label="First day of the week (the Tuesday)" type="date" value={f.startDate ?? ''}
          onChange={(v) => setF((p) => ({ ...p, startDate: v }))}
          help="Decides which phase everyone's checklist opens on." />
        <Text label="Course proposals due" type="date" value={f.deadlines.courses} onChange={(v) => dl('courses', v)} />
        <Text label="Independent project proposals due" type="date" value={f.deadlines.proposals} onChange={(v) => dl('proposals', v)} />
        <Text label="Student course selections due" type="date" value={f.deadlines.rankings} onChange={(v) => dl('rankings', v)} />
      </section>

      <section className="card" style={{ marginTop: 'var(--s-3)' }}>
        <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-3)' }}>Placement rules</h3>
        <Text label="Courses each student ranks in each block (morning and afternoon)" type="number"
          value={String(f.rules.ranksPerBlock ?? Math.max(1, Math.ceil((f.rules.ranksRequired || 6) / 2)))}
          onChange={(v) => setF((p) => { const n = Math.max(1, Number(v) || 1); return { ...p, rules: { ...p.rules, ranksPerBlock: n, ranksRequired: 2 * n } } })}
          help="Students rank this many morning courses and this many afternoon courses. Lower it if a block has fewer approved courses than this." />
        <Text label="Default course cap" type="number" value={String(f.rules.defaultCap)}
          onChange={(v) => rule('defaultCap', Number(v) || 1)} />
        <Select label="Who gets first pick"
          value={PRIORITY_LABEL[f.rules.placementPriority]}
          onChange={(v) => rule('placementPriority',
            (Object.keys(PRIORITY_LABEL) as Array<NtTerm['rules']['placementPriority']>)
              .find((k) => PRIORITY_LABEL[k] === v) ?? 'first-come')}
          options={Object.values(PRIORITY_LABEL)}
          help="First come, first served uses the Timestamp column of the course selection form export, so import it with the course selections. Students with no timestamp go last." />
      </section>

      <section className="card" style={{ marginTop: 'var(--s-3)' }}>
        <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-3)' }}>Lunch waves</h3>
        <Text label="Last name that ends the first wave" value={f.lunchWaves.cutoffLastName}
          onChange={(v) => setF((p) => ({ ...p, lunchWaves: { ...p.lunchWaves, cutoffLastName: v.toUpperCase() } }))}
          help="Students up to and including this name eat first." />
        <div className="grid2">
          <Text label="First wave" value={f.lunchWaves.a}
            onChange={(v) => setF((p) => ({ ...p, lunchWaves: { ...p.lunchWaves, a: v } }))} />
          <Text label="Second wave" value={f.lunchWaves.b}
            onChange={(v) => setF((p) => ({ ...p, lunchWaves: { ...p.lunchWaves, b: v } }))} />
        </div>
      </section>

      <div className="opt__bar">
        <button className="btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
      {savedAt && <p className="opt__hint" style={{ textAlign: 'center' }}>Saved at {savedAt.toLocaleTimeString()}</p>}
    </>
  )
}

const PRIORITY_LABEL: Record<NtTerm['rules']['placementPriority'], string> = {
  'first-come': 'First come, first served (form timestamp)',
  'seniors-first': 'Seniors first, then by rank',
  'by-rank': 'Purely by rank',
  'lottery': 'Lottery within rank',
}
